import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { livingSnapshot, DEFAULT_LOCATION, recommendedMotion, prefersReducedMotion } from '../lib/living';

/* The Living context follows the real sun, season and weather at the user's
 * location. It writes the sky palette to CSS variables on <html> so any
 * component (or plain CSS) can react, and exposes a snapshot via useLiving().
 */

const AMBIENT_KEY = 'gd_ambient';
const MOTION_KEY = 'gd_motion'; // 'full' | 'calm' | 'off'
const HOLO_KEY = 'gd_holo';     // 'on' | 'off'
const WEATHER_MAX_AGE_MS = 30 * 60 * 1000;

const LivingCtx = createContext(null);

function readAmbient() {
    try {
        const v = JSON.parse(localStorage.getItem(AMBIENT_KEY) || 'null');
        if (v && typeof v.lat === 'number' && typeof v.lon === 'number') {
            const age = Date.now() - v.weatherUpdatedAt;
            // Stored weather is not a forecast. Never replay yesterday's storm
            // while the first current-weather request is still pending.
            if (!Number.isFinite(v.weatherUpdatedAt) || age < 0 || age > WEATHER_MAX_AGE_MS) {
                return { ...v, weatherCode: null, wind: null, gusts: null, cloud: null, temp: null, weatherUpdatedAt: null };
            }
            return v;
        }
    } catch { /* ignore */ }
    return { ...DEFAULT_LOCATION };
}

function readMotion() {
    try {
        const v = localStorage.getItem(MOTION_KEY);
        if (v === 'full' || v === 'calm' || v === 'off') return v;
    } catch { /* ignore */ }
    return recommendedMotion();
}

function writeCssVars(s) {
    const root = document.documentElement;
    root.style.setProperty('--sky-top', s.sky.top);
    root.style.setProperty('--sky-bottom', s.sky.bottom);
    root.style.setProperty('--sky-glow', s.sky.glow);
    root.style.setProperty('--daylight', s.daylight.toFixed(3));
    root.style.setProperty('--wind', s.wind.toFixed(3));
    root.style.setProperty('--sun-x', `${s.sunX.toFixed(1)}%`);
    root.style.setProperty('--sun-y', `${s.sunY.toFixed(1)}%`);
    root.dataset.phase = s.phase;
    root.dataset.season = s.season;
    root.dataset.weather = s.mood;
    root.dataset.precip = s.wx.precip;
    root.dataset.icy = s.wx.icy ? '1' : '0';
    root.dataset.windy = s.wx.gale ? 'gale' : s.wx.windy ? '1' : '0';
    root.dataset.thunder = s.wx.thunder ? '1' : '0';
    root.style.setProperty('--precip', s.wx.intensity.toFixed(2));
    root.style.setProperty('--cloud', ((s.wx.cloud ?? 50) / 100).toFixed(2));
}

export function LivingProvider({ children }) {
    const [ambient, setAmbientState] = useState(readAmbient);
    const [motion, setMotionState] = useState(readMotion);
    const [reducedMotion, setReducedMotion] = useState(prefersReducedMotion);
    const [holo, setHoloState] = useState(() => {
        try { return localStorage.getItem(HOLO_KEY) === 'off' ? 'off' : 'on'; } catch { return 'on'; }
    });
    const [now, setNow] = useState(() => new Date());
    // Keep the user's saved choice, but the OS accessibility setting always
    // wins, including when it changes while the app is open.
    const effectiveMotion = reducedMotion ? 'off' : motion;

    useEffect(() => {
        let media;
        try { media = window.matchMedia('(prefers-reduced-motion: reduce)'); } catch { return undefined; }
        const update = () => setReducedMotion(media.matches);
        update();
        if (media.addEventListener) media.addEventListener('change', update);
        else media.addListener?.(update);
        return () => {
            if (media.removeEventListener) media.removeEventListener('change', update);
            else media.removeListener?.(update);
        };
    }, []);

    // The sun moves ~0.25° per minute — refresh once a minute and on resume.
    useEffect(() => {
        const id = setInterval(() => { if (!document.hidden) setNow(new Date()); }, 60000);
        const onVis = () => {
            document.documentElement.dataset.livingPaused = document.hidden ? '1' : '0';
            if (!document.hidden) setNow(new Date());
        };
        onVis();
        document.addEventListener('visibilitychange', onVis);
        return () => {
            clearInterval(id); document.removeEventListener('visibilitychange', onVis);
            delete document.documentElement.dataset.livingPaused;
        };
    }, []);

    const snapshot = useMemo(() => livingSnapshot(ambient, now), [ambient, now]);

    useEffect(() => { writeCssVars(snapshot); }, [snapshot]);

    useEffect(() => {
        document.documentElement.dataset.motion = effectiveMotion;
    }, [effectiveMotion]);

    useEffect(() => {
        document.documentElement.dataset.holo = holo;
    }, [holo]);

    const setHolo = useCallback((h) => {
        setHoloState(h);
        try { localStorage.setItem(HOLO_KEY, h); } catch { /* ignore */ }
    }, []);

    const setAmbient = useCallback((patch) => {
        setAmbientState((prev) => {
            const next = { ...prev, ...patch };
            if (Object.keys(next).every((k) => next[k] === prev[k])) return prev;
            try { localStorage.setItem(AMBIENT_KEY, JSON.stringify(next)); } catch { /* ignore */ }
            return next;
        });
    }, []);

    const setMotion = useCallback((m) => {
        setMotionState(m);
        try { localStorage.setItem(MOTION_KEY, m); } catch { /* ignore */ }
    }, []);

    const value = useMemo(
        () => ({ ...snapshot, ambient, setAmbient, motion: effectiveMotion, setMotion, holo, setHolo }),
        [snapshot, ambient, setAmbient, effectiveMotion, setMotion, holo, setHolo],
    );

    return <LivingCtx.Provider value={value}>{children}</LivingCtx.Provider>;
}

export function useLiving() {
    return useContext(LivingCtx) || {
        ...livingSnapshot({ ...DEFAULT_LOCATION }),
        ambient: DEFAULT_LOCATION,
        setAmbient: () => {},
        motion: 'full',
        setMotion: () => {},
        holo: 'on',
        setHolo: () => {},
    };
}
